import { computed, unref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAuthHelpers } from '@/authuser/helpers.js'
import { useAuthService } from '@/authuser/services'
import { useCurrentGroupRoles, useCurrentGroupService, useSanctionService } from '@/group/services'
import { usePlaceService } from '@/places/services.js'
import { useUserService } from '@/users/services.js'
import reactiveNow from '@/utils/reactiveNow.js'

export function useGroupHelpers () {
  const { groupId: currentGroupId } = useCurrentGroupService()

  function getIsCurrentGroup (group) {
    return currentGroupId.value === unref(group).id
  }

  return {
    getIsCurrentGroup,
  }
}

export function useMembershipHelpers () {
  const {
    group,
    roles,
  } = useCurrentGroupService()

  function getMembership (userId) {
    return group.value?.memberships.find(membership => membership.user === userId)
  }

  function getHasRole (userId, roleNameOrId) {
    if (!userId || !roleNameOrId) return false
    const role = roles.value.find(role => role.name === roleNameOrId || role.id === roleNameOrId)
    const membership = getMembership(userId)
    if (!membership || !role) return false
    return Boolean(membership.roles.find(entry => entry.role === role.id))
  }

  function createRoleGetter (roleName) {
    return userId => getHasRole(userId, roleName)
  }

  function authUserIs (roleName) {
    return computed(() => getHasRole(authUserId.value, roleName))
  }

  const getIsEditor = createRoleGetter('editor')

  const { userId: authUserId } = useAuthService()

  return {
    getMembership,
    getIsEditor,
    getIsNewcomer: createRoleGetter('newcomer'),
    getHasRole,

    // For auth user
    isEditor: authUserIs('editor'),
  }
}

export function useCurrentGroup () {
  const { group } = useCurrentGroupService()
  return group
}

export function useCurrentGroupId () {
  const { groupId } = useCurrentGroupService()
  return groupId
}

export function useIsEditor () {
  const { isEditor } = useMembershipHelpers()
  return isEditor
}

export function useRoleDisplayName (role) {
  const { getRoleDisplayName } = useRoleHelpers()
  return computed(() => getRoleDisplayName(unref(role)))
}

export function useRoleHelpers () {
  const { t } = useI18n()

  function getRoleDisplayName (role) {
    if (role.displayName) {
      return role.displayName
    }
    else if (role.nameIsTranslatable) {
      return t(`ROLES.${role.name.toUpperCase()}`)
    }
    return role.name
  }

  function getRoleSummary (role) {
    return role.summary || getRoleDescription(role)
  }

  function getRoleDescription (role) {
    if (role.description) {
      return role.description
    }
    else if (role.isSystemRole && ['member', 'newcomer', 'editor'].includes(role.name)) {
      return t(`ROLES.${role.name.toUpperCase()}_DESCRIPTION`)
    }
    return ''
  }

  return {
    getRoleDisplayName,
    getRoleSummary,
    getRoleDescription,
  }
}

export function useRolesFor (userId) {
  const { getMembership } = useMembershipHelpers()
  const roles = useCurrentGroupRoles()
  const membership = computed(() => getMembership(unref(userId)))

  return computed(() => roles.value
    .filter(role => !role.isArchived)
    .filter(role => membership.value?.roles.find(entry => entry.role === role.id)))
}

export function useRolesForCurrentUser () {
  const { userId } = useAuthService()
  return useRolesFor(userId)
}

export function useRoleMembershipsHelper () {
  const { getUserById } = useUserService()
  const { group } = useCurrentGroupService()
  const { getPlaceById } = usePlaceService()

  function getRole (id) {
    return group.value.roles.find(role => role.id === id)
  }

  function getMemberships (filterByUserId) {
    const userId = unref(filterByUserId)
    if (!userId) return group.value.memberships
    return group.value.memberships.filter(membership => membership.user === userId)
  }

  function getRoleMemberships (
    {
      roleId: filterByRoleId,
      userId: filterByUserId,
      contextPlaceId: filterByContextPlaceId,
    } = {}, {
      includeUser = true,
      includeContextPlace = true,
      includeRole = true,
    } = {},
  ) {
    const entries = []
    const roleId = unref(filterByRoleId)
    const contextPlaceId = unref(filterByContextPlaceId)
    for (const membership of getMemberships(filterByUserId)) {
      const user = includeUser ? getUserById(membership.user) : null
      const filteredRoleMemberships = membership.roles
        .filter(roleMembership => roleId ? roleMembership.role === roleId : true)
        .filter(roleMembership => contextPlaceId ? roleMembership.contextPlace === contextPlaceId : true)

      for (const roleMembership of filteredRoleMemberships) {
        entries.push({
          ...roleMembership,
          user: includeUser ? user : membership.user,
          role: includeRole ? getRole(roleMembership.role) : roleMembership.role,
          contextPlace: includeContextPlace ? getPlaceById(roleMembership.contextPlace) : roleMembership.contextPlace,
        })
      }
    }
    return entries
  }

  return getRoleMemberships
}

export function useRoleMemberships ({
  roleId,
  userId,
  contextPlaceId,
} = {}, {
  includeUser = true,
  includeContextPlace = true,
  includeRole = true,
} = {}) {
  const getRoleMemberships = useRoleMembershipsHelper()
  return computed(() => getRoleMemberships(
    { roleId, userId, contextPlaceId },
    { includeUser, includeContextPlace, includeRole },
  ))
}

/**
 * Gets roles and their memberships for a given place
 */
export function useRolesForPlace (place) {
  const { roles } = useCurrentGroupService()
  const placeId = computed(() => unref(place).id)
  const placeTypeId = computed(() => unref(place).placeType)
  return computed(() => roles.value
    .filter(role => !role.isArchived)
    .filter(role => role.scopes.find(scope => scope.place === placeId.value || scope.placeType === placeTypeId.value)))
}

// Order roles in this order (reversed), then alphabetically if not in this list
const reverseRoleOrder = ['mediator', 'editor', 'approved', 'newcomer', 'member']
export function sortByRoleOrder (a, b) {
  const aScore = reverseRoleOrder.indexOf(a.name)
  const bScore = reverseRoleOrder.indexOf(b.name)
  return aScore === bScore ? a.name.toLowerCase().localeCompare(b.name.toLowerCase()) : (aScore > bScore ? -1 : 1)
}

/**
 * Find the most specific role scope that matches a place, or null
 */
export function getRoleScope (roleScopes, place) {
  if (!place) return null
  const placeScopes = roleScopes.filter(scope => scope.place)

  for (const scope of placeScopes) {
    if (scope.place === place.id) {
      return scope
    }
  }

  const placeTypeScopes = roleScopes.filter(scope => scope.placeType)

  for (const scope of placeTypeScopes) {
    if (scope.placeType === place.placeType) {
      return scope
    }
  }
  return null
}

export function useTrustHeadline ({ role, group, user, trustedBy }) {
  const { getIsCurrentUser } = useAuthHelpers()
  const isEditorRole = computed(() => role.value.name === 'editor')
  const { t } = useI18n()
  const isCurrentUser = computed(() => getIsCurrentUser(user.value))

  return computed(() => {
    const otherTrust = trustedBy.value.filter(user => !getIsCurrentUser(user))
    const youTrust = trustedBy.value.find(user => getIsCurrentUser(user))

    // Historically "editor" was special and has its own translation paths
    const i18nBase = isEditorRole.value ? 'USERDATA' : 'TRUST_FOR_ROLE'

    const firstTrustMessage = () => t(`${i18nBase}.FIRST_TRUST`, {
      groupName: group.value.name,
      userName: user.value.displayName,
    })

    const trustMessagePath = () => {
      if (isCurrentUser.value) return `${i18nBase}.OTHER_PEOPLE_TRUST_YOU`
      if (!youTrust) return `${i18nBase}.OTHER_PEOPLE_TRUST_USER`
      if (otherTrust.length === 0) return `${i18nBase}.YOU_TRUST_USER`
      return `${i18nBase}.PEOPLE_TRUST_USER`
    }

    const trustMessage = () => t(trustMessagePath(), otherTrust.length, {
      named: {
        count: otherTrust.length,
        groupName: group.value.name,
        userName: user.value.displayName,
        otherUser: otherTrust.length > 0 && otherTrust[0].displayName,
      },
    })

    return (trustedBy.value.length > 0 || isCurrentUser.value) ? trustMessage() : firstTrustMessage()
  })
}

export function useGetGroupSanction () {
  const { groupSanctions } = useSanctionService()
  return groupId => {
    const sanctions = groupSanctions.value[unref(groupId)]
    if (!sanctions) return null
    return sanctions[0] // they are already sorted, so this is the biggest sanction
  }
}

export function useHasGroupSanction () {
  const { groupSanctions } = useSanctionService()
  return groupId => groupId in groupSanctions.value
}

export function useGroupSanction (groupId) {
  const getGroupSanction = useGetGroupSanction()
  return computed(() => getGroupSanction(groupId))
}

export function useRoleSanction (roleId) {
  const { roleSanctions } = useSanctionService()
  return computed(() => {
    const sanctions = roleSanctions.value[unref(roleId)]
    if (!sanctions) return null
    return sanctions[0] // they are already sorted, so this is the biggest sanction
  })
}

export function useGroupHasMediators () {
  const roles = useCurrentGroupRoles()
  const mediatorRole = computed(() => roles.value.find(role => role.name === 'mediator'))
  const mediatorRoleId = computed(() => mediatorRole.value?.id)
  const mediatorRoleMemberships = useRoleMemberships({ roleId: mediatorRoleId })
  return computed(() => mediatorRoleMemberships.value.length > 0)
}

export function useSanctionRestrictedMessageHelper () {
  const { t, d } = useI18n()
  return function sanctionRestrictedMessage (sanction) {
    if (!sanction || !sanctionIsActive(sanction)) return ''
    if (sanction.expiresAt) {
      return t('SANCTIONS.YOU_ARE_RESTRICTED_UNTIL', { date: d(sanction.expiresAt, 'yearMonthDayNumeric') })
    }
    return t('SANCTIONS.YOU_ARE_RESTRICTED')
  }
}

export function useSanctionRestrictedMessage (sanction) {
  const getMessage = useSanctionRestrictedMessageHelper()
  return computed(() => getMessage(unref(sanction)))
}

export function sanctionIsActive (sanction) {
  // Checking against both the reactive now and current date, as reactive now only updates periodically so
  // can be in the past for a short time, and sometimes we want to know for sure if it's active RIGHT NOW
  return !sanction.expiresAt || (sanction.expiresAt > reactiveNow.value && sanction.expiresAt > new Date())
}
